import AddOutlined from '@mui/icons-material/AddOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useState,
} from 'react';
import { FilterOptionMenu } from '../filters/FilterOptionMenu.js';
import type { ScopeOption } from '../filters/types.js';
import { FieldHeader, FieldTooltip } from './FieldHeader.js';
import { formatNumber, fromDisplayNumber, toDisplayNumber } from './fieldFormat.js';
import { FIELD_DEFAULT_LABEL, FIELD_WIDTH, TEXT_FIELD_WIDTH } from './fieldLayout.js';
import { activeExclusiveOption, type FieldValue, type FieldValues } from './fieldValues.js';
import { LookupControl } from './LookupControl.js';
import { LookupSelect } from './LookupSelect.js';
import { PresetCardsField } from './PresetCardsField.js';
import type { FieldOptions, FieldOptionsResult } from './useFieldOptions.js';
import type {
  CollapsibleField as CollapsibleFieldData,
  ExclusiveGroupField as ExclusiveGroupFieldData,
  MultiselectField as MultiselectFieldData,
  NumberField as NumberFieldData,
  SegmentedField as SegmentedFieldData,
  SelectField as SelectFieldData,
  TextField as TextFieldData,
  WorkflowField,
} from './workflows.types.js';

interface FieldControlProps {
  field: WorkflowField;
  values: FieldValues;
  optionsFor: FieldOptions;
  onChange: (key: string, value: FieldValue) => void;
  hideLabel?: boolean;
}

function NumberField({
  field,
  value,
  onChange,
  hideLabel = false,
}: {
  field: NumberFieldData;
  value: number | null;
  onChange: (value: number) => void;
  hideLabel?: boolean;
}): ReactNode {
  const [draft, setDraft] = useState<string | null>(null);
  const stepFrom = value ?? field.min ?? 0;
  const clamp = (next: number): number => {
    const floored = Math.max(field.min ?? 0, next);
    return field.max === null ? floored : Math.min(field.max, floored);
  };
  const shown = draft ?? formatNumber(field, value);
  const commit = (): void => {
    if (draft === null) return;
    const parsed = Number(draft);
    if (draft.trim() !== '' && Number.isFinite(parsed)) {
      onChange(clamp(fromDisplayNumber(field, parsed)));
    }
    setDraft(null);
  };

  return (
    <Stack sx={{ gap: 1.5, alignItems: 'flex-start' }}>
      {hideLabel ? null : <FieldHeader label={field.label} tooltip={field.tooltip} />}
      <Stack
        direction="row"
        sx={{
          alignItems: 'stretch',
          height: 34,
          maxWidth: FIELD_WIDTH,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'border.strong',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <IconButton
          size="small"
          aria-label={`Decrease ${field.label}`}
          disabled={value === null || value <= (field.min ?? 0)}
          onClick={() => onChange(clamp(stepFrom - field.step))}
        >
          <RemoveOutlined sx={{ fontSize: 14 }} />
        </IconButton>
        <Box
          component="input"
          type="text"
          inputMode="decimal"
          aria-label={field.label}
          size={Math.max(shown.length + 1, 3)}
          value={shown}
          onFocus={(event: FocusEvent<HTMLInputElement>) => {
            const input = event.currentTarget;
            setDraft(value === null ? '' : String(toDisplayNumber(field, value)));
            // Deferred: the draft re-render resets the value, which would drop a sync selection.
            requestAnimationFrame(() => input.select());
          }}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') event.currentTarget.blur();
            if (event.key === 'Escape') setDraft(null);
          }}
          sx={{
            boxSizing: 'content-box',
            px: 1.5,
            minWidth: 35,
            whiteSpace: 'nowrap',
            borderLeft: '1px solid',
            borderRight: '1px solid',
            borderTop: 0,
            borderBottom: 0,
            borderColor: 'divider',
            outline: 'none',
            bgcolor: 'transparent',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            textAlign: 'center',
            color: value === null ? 'text.disabled' : 'text.primary',
          }}
        />
        <IconButton
          size="small"
          aria-label={`Increase ${field.label}`}
          disabled={value !== null && field.max !== null && value >= field.max}
          onClick={() => onChange(clamp(stepFrom + field.step))}
        >
          <AddOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
    </Stack>
  );
}

function SelectField({
  field,
  options,
  value,
  hideLabel = false,
  onChange,
}: {
  field: SelectFieldData;
  options: ScopeOption[];
  value: string;
  hideLabel?: boolean;
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <Stack sx={{ gap: 1.5, alignItems: 'flex-start' }}>
      {hideLabel ? null : <FieldHeader label={field.label} tooltip={field.tooltip} />}
      <Select
        size="small"
        displayEmpty
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ 'aria-label': field.label }}
        sx={{
          width: FIELD_WIDTH,
          '& .MuiSelect-select': {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            color: value === '' ? 'text.disabled' : 'inherit',
          },
        }}
        renderValue={(selected) =>
          selected === ''
            ? (field.placeholder ?? FIELD_DEFAULT_LABEL)
            : (options.find((option) => option.id === selected)?.label ?? selected)
        }
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}

function SegmentedFieldControl({
  field,
  options,
  value,
  hideLabel = false,
  onChange,
}: {
  field: SegmentedFieldData;
  options: ScopeOption[];
  value: string;
  hideLabel?: boolean;
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <Stack sx={{ gap: 1.5, alignItems: 'flex-start' }}>
      {hideLabel ? null : <FieldHeader label={field.label} tooltip={field.tooltip} />}
      <Stack
        direction="row"
        role="radiogroup"
        aria-label={field.label}
        sx={{ gap: 1, flexWrap: 'wrap' }}
      >
        {options.map((option) => {
          const selected = option.id === value;
          return (
            <Box
              key={option.id}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option.id)}
              sx={{
                px: 2.5,
                py: 1,
                border: '1px solid',
                borderColor: selected ? 'transparent' : 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                bgcolor: selected ? 'background.icon' : 'transparent',
                color: selected ? 'common.white' : 'text.secondary',
              }}
            >
              {option.label}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}

function MultiselectFieldControl({
  field,
  resolved,
  values,
  hideLabel = false,
  onChange,
}: {
  field: MultiselectFieldData;
  resolved: FieldOptionsResult;
  values: string[];
  hideLabel?: boolean;
  onChange: (values: string[]) => void;
}): ReactNode {
  const { options } = resolved;
  const label = (id: string): string => options.find((option) => option.id === id)?.label ?? id;
  const [first, ...rest] = values;
  const chosen =
    first === undefined
      ? undefined
      : rest.length === 0
        ? label(first)
        : `${label(first)} +${rest.length}`;

  return (
    <LookupControl
      label={field.label}
      tooltip={field.tooltip}
      chosen={chosen}
      resolved={resolved}
      hideLabel={hideLabel}
    >
      {() => (
        <FilterOptionMenu
          label={field.label}
          options={options}
          selected={values}
          onChange={onChange}
          onSearchChange={resolved.onSearchChange}
        />
      )}
    </LookupControl>
  );
}

function TextFieldControl({
  field,
  value,
  hideLabel = false,
  onChange,
}: {
  field: TextFieldData;
  value: string;
  hideLabel?: boolean;
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <Stack sx={{ gap: 1.5 }}>
      {hideLabel ? null : <FieldHeader label={field.label} tooltip={field.tooltip} />}
      <TextField
        size="small"
        multiline={field.kind === 'textarea'}
        {...(field.kind === 'textarea' ? { minRows: 3 } : { sx: { maxWidth: TEXT_FIELD_WIDTH } })}
        value={value}
        placeholder={field.placeholder ?? undefined}
        onChange={(event) => onChange(event.target.value)}
        slotProps={{ htmlInput: { 'aria-label': field.label } }}
      />
    </Stack>
  );
}

function ExclusiveGroupField({
  field,
  values,
  hideLabel = false,
  onChange,
}: {
  field: ExclusiveGroupFieldData;
  values: FieldValues;
  hideLabel?: boolean;
  onChange: (key: string, value: FieldValue) => void;
}): ReactNode {
  const active = activeExclusiveOption(field, values);
  if (active === undefined) return null;
  const value = values[active.key] ?? active.default;

  return (
    <Stack sx={{ gap: 1.5, alignItems: 'flex-start' }}>
      {hideLabel ? null : <FieldHeader label={field.label} tooltip={field.tooltip} />}
      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={active.key}
          onChange={(_, next: string | null) => next !== null && onChange(field.key, next)}
        >
          {field.options.map((option) => (
            <ToggleButton key={option.key} value={option.key}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <NumberField
          field={active}
          hideLabel
          value={value === undefined || value === null ? null : Number(value)}
          onChange={(next) => onChange(active.key, next)}
        />
      </Stack>
      {field.hint === null ? null : (
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{field.hint}</Typography>
      )}
    </Stack>
  );
}

function ThresholdRow({
  field,
  children,
}: {
  field: WorkflowField;
  children: ReactNode;
}): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{
        gap: 4,
        px: 3.2,
        py: 2.4,
        alignItems: 'center',
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{field.label}</Typography>
        {field.tooltip === null ? null : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{field.tooltip}</Typography>
        )}
      </Stack>
      <Box sx={{ flex: 'none' }}>{children}</Box>
    </Stack>
  );
}

function CollapsibleField({
  field,
  values,
  optionsFor,
  onChange,
}: {
  field: CollapsibleFieldData;
  values: FieldValues;
  optionsFor: FieldOptions;
  onChange: (key: string, value: FieldValue) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);

  return (
    <Stack sx={{ width: '100%', gap: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
      <ButtonBase
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        sx={{ gap: 1.5, justifyContent: 'flex-start', alignSelf: 'flex-start' }}
      >
        <ExpandMoreOutlined
          sx={{
            fontSize: 16,
            color: 'text.disabled',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform .16s',
          }}
        />
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{field.label}</Typography>
        {field.hint === null ? null : (
          <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{field.hint}</Typography>
        )}
        <FieldTooltip tooltip={field.tooltip} />
      </ButtonBase>

      {open ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {field.fields.map((nested) => (
            <ThresholdRow key={nested.key} field={nested}>
              <WorkflowFieldControl
                field={nested}
                values={values}
                optionsFor={optionsFor}
                onChange={onChange}
                hideLabel
              />
            </ThresholdRow>
          ))}
        </Box>
      ) : null}
    </Stack>
  );
}

export function WorkflowFieldControl({
  field,
  values,
  optionsFor,
  onChange,
  hideLabel = false,
}: FieldControlProps): ReactNode {
  if (field.kind === 'collapsible') {
    return (
      <CollapsibleField field={field} values={values} optionsFor={optionsFor} onChange={onChange} />
    );
  }

  if (field.kind === 'exclusiveGroup') {
    return (
      <ExclusiveGroupField
        field={field}
        values={values}
        hideLabel={hideLabel}
        onChange={onChange}
      />
    );
  }

  // Drawn by the step itself: collecting files is not a value a control edits.
  if (field.kind === 'upload') return null;

  const value = values[field.key] ?? field.default;

  if (field.kind === 'presetCards') {
    return (
      <PresetCardsField
        field={field}
        value={String(value)}
        onChange={(next) => onChange(field.key, next)}
      />
    );
  }

  if (field.kind === 'number') {
    return (
      <NumberField
        field={field}
        value={value === undefined || value === null ? null : Number(value)}
        hideLabel={hideLabel}
        onChange={(next) => onChange(field.key, next)}
      />
    );
  }

  if (field.kind === 'segmented') {
    return (
      <SegmentedFieldControl
        field={field}
        options={optionsFor(field).options}
        value={String(value)}
        hideLabel={hideLabel}
        onChange={(next) => onChange(field.key, next)}
      />
    );
  }

  if (field.kind === 'multiselect') {
    return (
      <MultiselectFieldControl
        field={field}
        resolved={optionsFor(field)}
        values={Array.isArray(value) ? value : []}
        hideLabel={hideLabel}
        onChange={(next) => onChange(field.key, next)}
      />
    );
  }

  if (field.kind === 'text' || field.kind === 'textarea') {
    return (
      <TextFieldControl
        field={field}
        value={String(value)}
        hideLabel={hideLabel}
        onChange={(next) => onChange(field.key, next)}
      />
    );
  }

  const resolved = optionsFor(field);
  if (field.optionsSource !== null) {
    return (
      <LookupSelect
        label={field.label}
        tooltip={field.tooltip}
        resolved={resolved}
        value={String(value)}
        hideLabel={hideLabel}
        onChange={(next) => onChange(field.key, next)}
      />
    );
  }

  return (
    <SelectField
      field={field}
      options={resolved.options}
      value={String(value)}
      hideLabel={hideLabel}
      onChange={(next) => onChange(field.key, next)}
    />
  );
}
